import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { Row, Column, List, Alert, Modal, TextInput, Fieldset, Checkbox, Button } from '@pella/sash-framework';
import { Image, CTA } from '../../../';
import { initDataLayer, setCookie, getCookie, savePostalCode } from '../../../utilities/functions';
import { getMarket, getBranch, getServiceInfo, getShowroom } from '../../../../actions/location-actions';
import BaseCard from '../../../common/card';
import EloquaForm from '../../../common/eloqua-form';
import styles from './index.module.scss';
import { isValidArray, isValidEmail, isValidPostal } from '../../../utilities/validations';
import { eloquaTrackingUrl } from '../../../utilities/constants';

const BrochureCard = ({ data, children, mailMe = false, onMailMeClick, ...other }) => {
	const dispatch = useDispatch();
	const [showModal, setShowModal] = useState(false);
	const [values, setValues] = useState({ email: '', zip: '' });
	const [errors, setErrors] = useState({});
	const [success, setSuccess] = useState('');

	const onModalTrigger = (event, heading, url) => {
		event.preventDefault();

		if (getCookie('subscribed') == null) {
			setShowModal(heading);
		} else {
			window.open(url, '_blank');
		}

		initDataLayer('brochure-download', {
			brochureName: heading,
		});
	};

	const onEmailBlur = (e) => {
		if (!isValidEmail(e.target.value)) {
			setErrors({ email: 'Please enter a valid email address' });
		}
	};

	const onModalDownload = (link) => {
		if (isValidEmail(values.email) && isValidPostal(values.zip)) {
			// Success
			const data = new FormData(document.getElementById('brochure-download'));

			axios
				.post(eloquaTrackingUrl, data)
				.then((res) => {
					if (res.status === 200) {
						setErrors(false);
						setSuccess('Success! You are now subscribed to Pella offers and inspiration.');
						setShowModal(false);
						savePostalCode(values.zip);
						dispatch(getMarket(values.zip));
						dispatch(getBranch(values.zip));
						dispatch(getShowroom(values.zip));
						dispatch(getServiceInfo(values.zip));
						setCookie('subscribed', true, 31536000000);
						window.open(link, '_blank');
					}
				})
				.catch();
		} else if (isValidEmail(values.email) && !isValidPostal(values.zip)) {
			setErrors({ zip: 'Please enter a valid postal code' });
		} else if (!isValidEmail(values.email) && isValidPostal(values.zip)) {
			setErrors({ email: 'Please enter a valid email address' });
		} else {
			setErrors({
				email: 'Please enter a valid email address',
				zip: 'Please enter a valid postal code',
			});
		}
	};

	return (
		<>
			<BaseCard
				className={styles.broCard}
				{...other}
			>
				{success && (
					<Alert
						className='mb-4'
						type='success'
						onClose={() => setSuccess('')}
					>
						{success}
					</Alert>
				)}

				<Row>
					<Column xs='auto'>
						{data.image?.[0] && (
							<Image
								data={data.image[0]}
								className={styles.broCard_Image}
							/>
						)}
					</Column>

					<Column xs='fluid'>
						<div className={styles.broCard_Flex}>
							{data.heading && <h3 className='s2-h3__alt'>{data.heading}</h3>}

							{data.description && <p className={styles.broCard_Description}>{data.description}</p>}

							{isValidArray(data?.ctas) && (
								<List bullets='none'>
									{data.ctas.map((link, index) => (
										<li key={index}>
											<Row>
												<Column xs='auto'>
													<CTA
														data={link}
														data-heading={data.heading}
														onClick={(event) => onModalTrigger(event, data.heading, link.link?.href)}
														renderAs='link'
													>
														PDF
													</CTA>
												</Column>

												{mailMe && data.mail_me && (
													<Column xs='auto'>
														<div className={styles.broCard_PlaceOrderButton}>
															<Button onClick={onMailMeClick}>Mail Me</Button>
														</div>
													</Column>
												)}
											</Row>
										</li>
									))}
								</List>
							)}
						</div>
					</Column>
				</Row>
			</BaseCard>

			<Modal
				isOpen={showModal === data.heading}
				onClose={() => {
					setErrors(false);
					setValues({ email: '', zip: '' });
					setShowModal(false);
				}}
				width={800}
			>
				<Modal.Content>
					<h3 className={`${styles.modal_Header} s2-h3__alt`}>
						Start Downloading<span>{data.heading}</span>
					</h3>

					<EloquaForm
						id='brochure-download'
						elqFormName='brochure-download'
						onFormSubmit={(e) => {
							e.preventDefault();
							onModalDownload(data.ctas[0].link?.href);
						}}
						className={styles.modal_Form}
					>
						<input
							type='hidden'
							name='brochureName'
							value={data.heading}
						/>

						<Row>
							<Column
								xs={12}
								sm={6}
							>
								<TextInput
									type='email'
									id='downloadEmail'
									placeholder='Enter your email address'
									theme='redesign'
									labelText='Email Address'
									onChange={(e) =>
										setValues({
											...values,
											email: e.target.value,
										})
									}
									onBlur={(e) => onEmailBlur(e)}
									error={errors.email}
									errorText={errors.email}
									required
								/>
							</Column>

							<Column
								xs={12}
								sm={6}
							>
								<TextInput
									id='downloadZip'
									placeholder='Enter your postal code'
									theme='redesign'
									labelText='Postal Code'
									onChange={(e) =>
										setValues({
											...values,
											zip: e.target.value,
										})
									}
									error={errors.zip}
									errorText={errors.zip}
									required
								/>
							</Column>

							<Column xs={12}>
								<p>Would you like to be notified of Pella product updates or special offers via email?</p>

								<Fieldset
									legendText='Email Notification'
									hideLegend
								>
									<Checkbox
										type='radio'
										id='yes'
										name='emailNotification'
										labelText='Yes'
										value='Yes'
										defaultChecked
									/>

									<Checkbox
										type='radio'
										id='no'
										name='emailNotification'
										labelText='No'
										groupClass='ml-2'
										value='No'
									/>
								</Fieldset>
							</Column>
						</Row>

						<Button
							type='submit'
							color='primary-alt'
							data-gtm-linktype='brochure'
							data-gtm-linktext={data.heading}
						>
							Download Brochure
						</Button>
					</EloquaForm>

					<p className={styles.modal_FootNote}>You can opt-out at any time.</p>
				</Modal.Content>
			</Modal>
		</>
	);
};

BrochureCard.propTypes = {
	data: PropTypes.object,
	children: PropTypes.node,
	mailMe: PropTypes.bool,
	onMailMeClick: PropTypes.func,
};

export default BrochureCard;
